import type { PatchRequest } from '@shared/patch-request';
import { withTimeLimit } from '@/lib/time-limit';

/**
 * SF-T-153: how long the browser waits on Apply, Apply all and Push before it stops waiting and reads
 * where the requests stand. The server waits up to 28 s for the machine (APPLY_TIMEOUT_MS in
 * worker/src/services/runs/patch-requests.ts), so this leaves it room to answer.
 */
export const PATCH_APPLY_TIME_LIMIT_MS = 45_000;

/** How long the read after a time-out may take: whatever hung the Apply may hang it too. */
export const PATCH_RECOVERY_TIME_LIMIT_MS = 15_000;

/** `reload`, given up on after PATCH_RECOVERY_TIME_LIMIT_MS: null when it failed or took too long. */
export async function reloadWithin<T>(reload: (() => Promise<T | null>) | undefined, ms: number = PATCH_RECOVERY_TIME_LIMIT_MS): Promise<T | null> {
  if (!reload) return null;
  const out = await withTimeLimit(() => reload().catch(() => null), ms);
  return out.timedOut ? null : out.res;
}

/** Where one request stands, said neutrally: after the browser stopped waiting on it. */
function standing(p: PatchRequest): string {
  if (p.status === 'applied') return p.pushed ? 'Applied and pushed' : 'Applied, but not pushed yet';
  if (p.status === 'applying') return 'Still applying: this updates when it lands';
  if (p.status === 'rejected') return 'Rejected';
  return p.error ? `Not applied: ${p.error}` : 'Not applied yet: it still waits for an admin';
}

/**
 * The note shown when the browser stopped waiting (SF-T-153): what the reloaded requests say about the
 * ones it was applying (`ids`). Null when they couldn't be read.
 */
export function timedOutNote(ids: string[], requests: PatchRequest[] | null): string {
  const mine = (requests ?? []).filter((p) => ids.includes(p.id));
  if (!mine.length) return "No answer yet, and the patch requests couldn't be read again. They update here when the factory hears back.";
  const notes = new Set(mine.map(standing));
  if (notes.size === 1) return standing(mine[0]!);
  return mine.map((p) => `${p.files[0] ?? 'patch'}: ${standing(p)}`).join(' · ');
}

/** How soon after a hold ends to read again, and the least wait between such reads (clocks differ). */
const SETTLE_MARGIN_MS = 3_000;

/**
 * SF-T-153: when the first applying request's hold ends and it turns retryable (Apply again), as epoch
 * ms; null when none is waiting for that. The live hub sends nothing then, so the dialog reads again.
 */
export function retryableAt(requests: PatchRequest[]): number | null {
  const ends = requests
    .filter((p) => p.status === 'applying' && !p.retryable && p.applying_until)
    .map((p) => Date.parse(p.applying_until!))
    .filter((t) => Number.isFinite(t));
  return ends.length ? Math.min(...ends) : null;
}

/** How long to wait from `now` before reading again for `at` (retryableAt): a little after it, never less than the margin. */
export function settleDelay(at: number, now: number): number {
  return Math.max(at - now + SETTLE_MARGIN_MS, SETTLE_MARGIN_MS);
}

/** The part of a request that decides its footer: a change means the button's own waiting gives way. */
export function footerState(p: PatchRequest): string {
  return `${p.status}:${p.pushed}:${p.retryable}`;
}
